import { yieldToMainThread, prefersReducedMotion } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * Point on a cubic Bézier from P0→P3 with control points P1, P2 (t ∈ [0, 1]).
 *
 * @param {number} t
 * @param {{ x: number; y: number }} p0
 * @param {{ x: number; y: number }} p1
 * @param {{ x: number; y: number }} p2
 * @param {{ x: number; y: number }} p3
 */
function cubicBezierPoint(t, p0, p1, p2, p3) {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  const uuu = uu * u;
  const ttt = tt * t;
  return {
    x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y,
  };
}

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    const intersectionObserver = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;

      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        this.#animate(sourceRect, destinationRect);
      }

      intersectionObserver.disconnect();
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }

    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);

    const duration = this.classList.contains('fly-to-cart--sticky') ? 800 : 600;

    // Position before paint (CSS travel-x/y animations removed — path is driven by WAAPI below).
    this.style.translate = `${startPoint.x}px ${startPoint.y}px`;

    await yieldToMainThread();

    if (prefersReducedMotion()) {
      this.style.translate = `${endPoint.x}px ${endPoint.y}px`;
    } else {
      const sx = startPoint.x;
      const sy = startPoint.y;
      const ex = endPoint.x;
      const ey = endPoint.y;
      const verticalGap = Math.max(0, sy - ey);

      // Rise vertically first, then bend toward the cart (avoids X easing overshoot flying off-screen left).
      const lift = Math.min(Math.max(72, verticalGap * 0.55 + 40), window.innerHeight * 0.22);

      const p0 = { x: sx, y: sy };
      const p1 = { x: sx, y: sy - lift };
      const p2 = {
        x: ex,
        y: Math.min(sy - lift * 0.5, ey + Math.max(20, verticalGap * 0.12)),
      };
      const p3 = { x: ex, y: ey };

      const steps = 24;
      /** @type {Keyframe[]} */
      const translateKeyframes = [];
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const p = cubicBezierPoint(t, p0, p1, p2, p3);
        translateKeyframes.push({
          translate: `${p.x}px ${p.y}px`,
          offset: t,
        });
      }

      this.animate(translateKeyframes, {
        duration,
        fill: 'forwards',
        easing: 'linear',
      });
    }

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
